import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DialogClose } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';
import { SearchableLocationSelect } from '@/lib/pages/forms/components/LocationSelect';

import {
  glacierPolicyApiAssignMutation,
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetPolicySummaryQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type { UserSchema2 } from '@/lib/heyapi/types.gen';

type TransferQuote = {
  id: string;
};

export const TransferView = ({
  related,
  accountId,
}: {
  accountId: string;
  related: Array<UserSchema2>;
}) => {
  const qc = useQueryClient();
  const permissionsQk = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: { account_id: accountId },
  });
  const summariesQk = glacierPolicyApiGetPolicySummaryQueryKey();
  const policyQk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const transfer = useMutation({
    ...glacierPolicyApiAssignMutation(),
    onSuccess() {
      qc.refetchQueries({ queryKey: permissionsQk });
      qc.refetchQueries({ queryKey: summariesQk });
      qc.refetchQueries({ queryKey: policyQk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Transferred</code>
          </pre>
        ),
      });
    },
  });

  const form = useForm<TransferQuote>({
    defaultValues: {
      id: undefined,
    },
  });

  const { control, handleSubmit, watch } = form;
  const watchId = watch('id');

  const selectable = related
    .map((u) => ({
      label: `${u.firstName} ${u.lastName} <${u.email}>`,
      value: String(u.id!),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const onSubmit: SubmitHandler<TransferQuote> = (data) => {
    transfer.mutate({
      path: {
        account_id: accountId,
      },
      body: {
        userId: Number(data.id),
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Transfer</CardTitle>
          </CardHeader>
          <CardContent>
            <SearchableLocationSelect
              control={control}
              name="id"
              options={selectable}
              label=""
              searchPlaceholder="Search Agents"
              placeholder="Choose an Agent or Underwriter"
              emptyMessage="No Agent or Underwriter found"
              ignoreReadMode={true}
            />
          </CardContent>
          <CardFooter className="flex justify-end">
            <DialogClose asChild>
              <Button
                type="submit"
                className="bg-brand-green text-brand-0 transition hover:bg-brand-green hover:opacity-70 hover:duration-300"
                disabled={!watchId}
              >
                Transfer
              </Button>
            </DialogClose>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};
